import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import { setCssVar } from 'quasar';
import { useAuthStore } from './auth';
import { useCompanyStore } from './company';

export interface CompanyBranding {
  primaryColor: string | null;
  secondaryColor: string | null;
  accentColor: string | null;
  backgroundColor: string | null;
  textColor: string | null;
  logoUrl: string | null;
}

// Applies a company's palette (core360_auth's company_branding table, via
// this module's own GET /api/companies/:id/branding proxy) as the active
// Quasar theme. Universal modules never call this - they keep the app's
// standard styling (see stores/auth.ts's moduleIsUniversal).
export const useBrandingStore = defineStore('branding', () => {
  const branding = ref<CompanyBranding | null>(null);
  const loaded = ref(false);

  const CSS_VAR_BY_FIELD: Record<'primaryColor' | 'secondaryColor' | 'accentColor' | 'backgroundColor' | 'textColor', string> = {
    primaryColor: 'primary',
    secondaryColor: 'secondary',
    accentColor: 'accent',
    backgroundColor: 'brand-background',
    textColor: 'brand-text',
  };

  function apply(b: CompanyBranding | null) {
    (Object.keys(CSS_VAR_BY_FIELD) as (keyof typeof CSS_VAR_BY_FIELD)[]).forEach((field) => {
      const value = b?.[field];
      // A missing field falls back to the app's default theme colour -
      // nothing is set, so the default declared in quasar.variables.scss wins.
      if (value) setCssVar(CSS_VAR_BY_FIELD[field], value);
    });
  }

  async function fetchBranding(companyId: number) {
    try {
      const token = useAuthStore().token;
      const res = await fetch(`/api/companies/${companyId}/branding`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      branding.value = (await res.json()) as CompanyBranding;
      apply(branding.value);
    } catch (err) {
      console.error('[Branding] Failed to fetch company branding:', err);
      branding.value = null;
      apply(null);
    } finally {
      loaded.value = true;
    }
  }

  // Re-applies whenever the selected company changes (e.g. a user with
  // access to multiple companies switches between them).
  watch(
    () => useCompanyStore().selectedCompanyId,
    (companyId) => {
      if (companyId !== null) void fetchBranding(companyId);
    },
    { immediate: true },
  );

  return { branding, loaded, fetchBranding };
});
